import { useEffect, useState } from "react";
import {
  Backdrop,
  Button,
  DevNote,
  Divider,
  LogoLoader,
  NewTableAdv,
  showToast,
  StatusTags,
} from "../../../components";
import { fetchWFNotifications, mapTableRows } from "../../../utils";
import { useLoginUser } from "../../../hooks/useLoginUser";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { TimesheetStatus } from "../../../assets/data/enumData";
import { useSelector } from "react-redux";
import { usePagination } from "../../../hooks";
import { updateTimesheet } from "../../../redux/slices/timesheets/timesheetApi";
import { exactUTCDate } from "../../../utils/dateFormator";
import { updateNotification, updateTotalCountToStore } from "../../../utils/wf-notifications";

const TimesheetNotification = ({ totalCount, fetchTotals }) => {
  const { userData: loginUserData } = useLoginUser();
  const loginUserUiid = loginUserData?.uiid;
  const [notificationData, setNotificationData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notificationIds, setNotificationIds] = useState([]);
  const notificationStore = useSelector((state) => state.wfNotification);
  console.log({ notificationData });

  const { page, limit, setPage, handleLimitChange } = usePagination(25);
  const colDetails = [
    { key: "title", label: "Title" },
    {
      key: "message",
      label: "Message",
      width: "200px",
      render: (row) => <div className="text-wrap">{row.message}</div>,
    },
    {
      key: "employeeName",
      label: "Employee Name",
      render: (row) => (
        <Link
          to="/employee/employee-profile/timesheet"
          state={{
            uiid: row?.meta?.id?.placementId,
            timesheetId: row?.meta?.id?.uiid,
            employeeId: row?.meta?.id?.employeeId?.uiid,
          }}
        >
          <div className="text-wrap text-linkblue">
            {(row?.meta?.id?.employeeId?.firstName || "") +
              " " +
              (row?.meta?.id?.employeeId?.lastName || "")}
          </div>
        </Link>
      ),
    },
    {
      key: "timesheetPeriod",
      label: "Timesheet Period",
      render: (row) => (
        <div className="text-wrap">
          {(exactUTCDate(row?.meta?.id?.period?.from) || "") +
            " - " +
            (exactUTCDate(row?.meta?.id?.period?.to) || "")}
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => {
        return (
          <StatusTags
            tag={
              TimesheetStatus.find(
                (status) => status.value === row?.meta?.id?.status,
              )?.label || "-"
            }
          />
        );
      },
    },
    {
      key: "totalHours",
      label: "Total Hours",
      render: (row) => (
        <div className="text-wrap">
          {row?.meta?.id?.dailyEntries?.reduce((a, b) => a + b.st, 0)}
        </div>
      ),
    },
  ];
  const fetchData = async () => {
    try {
      setIsLoading(true);
      const res = await fetchWFNotifications({
        limit: limit,
        page: page,
        filters: {
          userId: { type: "NONE", value: loginUserUiid },
          "meta.entity": { type: "NONE", value: "Timesheet" },
          "isRead.status": { type: "NONE", value: false },
        },
        sort: JSON.stringify({ addedOn: -1 }),
        ref: "TimesheetWithEmp",
        incl: ["period dailyEntries placementId status"],
      });
      setNotificationData(
        res?.notifications?.map((row) => ({
          // id: row?.meta?.id?.uiid,
          id: row?._id,
          ...row,
        })) || [],
      );
    } catch (error) {
      console.log("error in fetchData TimesheetNotification", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [loginUserUiid, notificationStore, page, limit]);

  const mappedrows = mapTableRows(notificationData, colDetails);

  const handleApprove = async () => {
    if (!notificationIds?.length) {
      showToast("No Timesheet Selected!", "warning", 5000);
      return;
    }
    const timesheetIds = [...new Set(notificationData?.filter((row) => notificationIds.includes(row?._id))?.filter(ts=> ts?.meta?.id?.status === 2)?.map(a => a?.meta?.id?.uiid))];
    // const uniqueTimesheetIds = [...new Set(timesheetIds)];
    console.log("timesheetIds", timesheetIds);
    // throw new Error("skhbsh");
    setIsLoading(true);
    try {
      const responses = await Promise.all(
        timesheetIds.map((uiid) =>
          updateTimesheet({
            coll: "Timesheet",
            uiid,
            payload: [
              {
                type: "SET",
                value: { status: 3 },
              },
            ],
          }),
        ),
      );

      console.log("Approve responses:", responses);
      const allSuccess = responses.every((r) => r?.success === true);
      const allFailed = responses.every((r) => r?.success === false);

      if (allSuccess) {
        showToast("All timesheets approved successfully", "success", 3000);
        notificationIds?.map((id) =>
          updateNotification({
            filter: { _id: id },
            payload: [{ type: "SET", value: { isRead: true } }],
          }),
        );
      } else if (allFailed) {
        showToast("All timesheets approval failed", "warning", 3000);
      } else {
        showToast("Some timesheets approval failed", "warning", 3000);
      }
      fetchData();
      fetchTotals();
    } catch (error) {
      console.error("Error in handleApprove", error);
      showToast("Failed to approve timesheets", "error", 5000);
    } finally {
      setIsLoading(false);
      updateTotalCountToStore({ userId: loginUserUiid });
    }
  };

  return (
    <>
      <DevNote>
        <>Total Unread Notifications: {notificationStore?.totalUnreadCount}</>
      </DevNote>
      <div className="px-3">
        <NewTableAdv
          showCheckbox
          columns={colDetails}
          rows={mappedrows}
          onSelectionChange={(ids) => setNotificationIds(ids || [])}
          tdClassName="!py-4"
          serverPagination={true}
          totalCount={totalCount}
          limit={limit}
          page={page}
          onPageChange={setPage}
          onLimitChange={handleLimitChange}
          toolbarRight={{
            search: true,
            add: [
              {
                component: (
                  <div className="flex items-center px-2">
                    <Divider orientation="vertical" />
                    {notificationIds?.length > 0 && (
                      <Button
                        type="button"
                        title="Approve"
                        variant="outlined"
                        onClick={handleApprove}
                        color="primary"
                        size="sm"
                      />
                    )}
                    <Divider orientation="vertical" />
                    <Button
                      type="icon"
                      icon={"refresh"}
                      color="black"
                      variant="text"
                      onClick={fetchData}
                    />
                  </div>
                ),
              },
            ],
          }}
        />
      </div>
      <Backdrop isOpen={isLoading}>
        <LogoLoader />
      </Backdrop>
    </>
  );
};

export default TimesheetNotification;
